import React from 'react';
import { Box, Stack, Grid, Paper, Typography, Divider } from '@mui/material';
import { Users, Clock, LineChart } from 'lucide-react';
import { IResultListData } from '../Hooks/UseAssesmentResultList';

interface IResultsStats {
  countCompleted: number;
  countTotal: number;
  calculateAverageTime: () => string;
  resultListData: IResultListData | undefined;
}

export const ResultsStats: React.FC<IResultsStats> = ({
  countCompleted,
  countTotal,
  calculateAverageTime,
  resultListData,
}) => {
  return (
    <Grid container spacing={2}>
      {/* Total Candidates Card */}
      <Grid item xs={12} sm={4}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: 'success.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'success.main',
              flexShrink: 0,
            }}
          >
            <Users size={18} />
          </Box>
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={500} display="block">
                Completed
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} color="success.main">
                {countCompleted}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ height: 24, my: 'auto' }} />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={500} display="block">
                Total
              </Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                {countTotal}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Grid>

      {/* Average Time Card */}
      <Grid item xs={12} sm={4}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: 'info.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'info.main',
              flexShrink: 0,
            }}
          >
            <Clock size={18} />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500} display="block">
              Average Time
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} color="info.main">
              {calculateAverageTime()}
            </Typography>
          </Box>
        </Paper>
      </Grid>

      {/* Highest Score Card */}
      <Grid item xs={12} sm={4}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: 'secondary.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'secondary.main',
              flexShrink: 0,
            }}
          >
            <LineChart size={18} />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500} display="block">
              Highest Score
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} color="secondary.main">
              {(() => {
                const score = resultListData?.highestScore;
                return isFinite(score as number)
                  ? Math.min(score as number, 100)
                  : 0;
              })()}
              %
            </Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};
